// Componentes del Módulo de Integraciones
// Este archivo contiene todos los componentes necesarios para el sistema de integraciones

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './components/ui/dialog';
import { Switch } from './components/ui/switch';
import { 
  Link, 
  Webhook, 
  Database, 
  Upload, 
  Activity, 
  BarChart3,
  Plus,
  Settings,
  Play,
  RefreshCw,
  Trash2,
  Eye,
  AlertCircle,
  CheckCircle,
  Clock,
  Users,
  TrendingUp,
  Download
} from 'lucide-react';

// Constantes (deben coincidir con las del App.js principal)
const INTEGRATION_TYPES = [
  'API REST', 'Webhook', 'File Upload', 'Facebook Ads', 'Google Ads', 
  'LinkedIn Ads', 'HubSpot', 'Mailchimp', 'Zapier', 'Custom'
];

const INTEGRATION_STATUS_COLORS = {
  'Active': 'bg-green-100 text-green-800 border-green-200',
  'Inactive': 'bg-gray-100 text-gray-800 border-gray-200',
  'Error': 'bg-red-100 text-red-800 border-red-200',
  'Testing': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'Configured': 'bg-blue-100 text-blue-800 border-blue-200'
};

const LEAD_PROCESSING_STATUS_COLORS = {
  'Pending': 'bg-gray-100 text-gray-800 border-gray-200',
  'Processing': 'bg-blue-100 text-blue-800 border-blue-200',
  'Completed': 'bg-green-100 text-green-800 border-green-200',
  'Failed': 'bg-red-100 text-red-800 border-red-200',
  'Duplicate': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'Invalid': 'bg-orange-100 text-orange-800 border-orange-200'
};

// Iconos por tipo de integración
const getIntegrationIcon = (type) => {
  switch (type) {
    case 'API REST': return <Database className="h-5 w-5" />;
    case 'Webhook': return <Webhook className="h-5 w-5" />;
    case 'File Upload': return <Upload className="h-5 w-5" />;
    case 'Facebook Ads': return <Activity className="h-5 w-5" />;
    case 'Google Ads': return <BarChart3 className="h-5 w-5" />;
    default: return <Link className="h-5 w-5" />;
  }
};

// Componente principal de integraciones
export const IntegrationsView = ({
  integrations = [],
  integrationDashboard = null,
  selectedIntegration = null,
  fieldMappings = [],
  incomingLeads = [],
  hasPermission,
  onCreateIntegration,
  onUpdateIntegrationStatus,
  onDeleteIntegration,
  onSelectIntegration,
  onCreateFieldMapping,
  onDeleteFieldMapping,
  onTestIntegration,
  onReprocessLead,
  setShowIntegrationDialog,
  setShowFieldMappingDialog,
  setShowTestIntegrationDialog
}) => {
  const [activeIntegrationTab, setActiveIntegrationTab] = React.useState('dashboard');

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-mg-green">Módulo de Integraciones</h1>
          <p className="text-gray-600 mt-2">Sistema genérico para leads automatizados de terceros</p>
        </div>
        {hasPermission(['Admin']) && (
          <Button
            onClick={() => setShowIntegrationDialog(true)}
            className="bg-mg-green hover:bg-mg-green/90 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nueva Integración
          </Button>
        )}
      </div>

      {/* Integration Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveIntegrationTab('dashboard')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeIntegrationTab === 'dashboard'
                ? 'border-mg-green text-mg-green'
                : 'border-transparent text-gray-500 hover:text-mg-green hover:border-mg-green/50'
            }`}
          >
            <BarChart3 className="h-4 w-4 mr-2 inline" />
            Dashboard
          </button>
          <button
            onClick={() => setActiveIntegrationTab('integrations')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeIntegrationTab === 'integrations'
                ? 'border-mg-green text-mg-green'
                : 'border-transparent text-gray-500 hover:text-mg-green hover:border-mg-green/50'
            }`}
          >
            <Link className="h-4 w-4 mr-2 inline" />
            Integraciones
          </button>
          <button
            onClick={() => setActiveIntegrationTab('leads')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeIntegrationTab === 'leads'
                ? 'border-mg-green text-mg-green'
                : 'border-transparent text-gray-500 hover:text-mg-green hover:border-mg-green/50'
            }`}
          >
            <Users className="h-4 w-4 mr-2 inline" />
            Leads Procesados
          </button>
          {hasPermission(['Admin']) && (
            <button
              onClick={() => setActiveIntegrationTab('mappings')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeIntegrationTab === 'mappings'
                  ? 'border-mg-green text-mg-green'
                  : 'border-transparent text-gray-500 hover:text-mg-green hover:border-mg-green/50'
              }`}
            >
              <Settings className="h-4 w-4 mr-2 inline" />
              Configuración
            </button>
          )}
        </nav>
      </div>

      {/* Tab Content */}
      {activeIntegrationTab === 'dashboard' && (
        <IntegrationDashboard 
          integrationDashboard={integrationDashboard} 
        />
      )}
      {activeIntegrationTab === 'integrations' && (
        <IntegrationsList 
          integrations={integrations}
          hasPermission={hasPermission}
          onUpdateStatus={onUpdateIntegrationStatus}
          onDelete={onDeleteIntegration}
          onTest={onTestIntegration}
          setShowTestIntegrationDialog={setShowTestIntegrationDialog}
        />
      )}
      {activeIntegrationTab === 'leads' && (
        <IncomingLeadsList 
          incomingLeads={incomingLeads}
          hasPermission={hasPermission}
          onReprocess={onReprocessLead}
        />
      )}
      {activeIntegrationTab === 'mappings' && hasPermission(['Admin']) && (
        <IntegrationConfiguration 
          integrations={integrations}
          selectedIntegration={selectedIntegration}
          fieldMappings={fieldMappings}
          onSelectIntegration={onSelectIntegration}
          onCreateFieldMapping={onCreateFieldMapping}
          onDeleteFieldMapping={onDeleteFieldMapping}
          setShowFieldMappingDialog={setShowFieldMappingDialog}
        />
      )}
    </div>
  );
};

// Dashboard de integraciones
const IntegrationDashboard = ({ integrationDashboard }) => {
  if (!integrationDashboard) {
    return <div>Cargando dashboard...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-mg-green">
                {integrationDashboard.total_integrations}
              </div>
              <div className="text-sm text-gray-600">Total Integraciones</div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-500">
                {integrationDashboard.active_integrations}
              </div>
              <div className="text-sm text-gray-600">Activas</div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-500">
                {integrationDashboard.leads_received_today}
              </div>
              <div className="text-sm text-gray-600">Leads Hoy</div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-mg-green">
                {integrationDashboard.overall_success_rate}%
              </div>
              <div className="text-sm text-gray-600">Éxito Global</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Processing Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-mg-green">Estadísticas de Procesamiento</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Leads Totales:</span>
                <span className="font-semibold">{integrationDashboard.total_leads_processed}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Exitosos:</span>
                <span className="font-semibold text-green-500">{integrationDashboard.successful_leads}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Fallidos:</span>
                <span className="font-semibold text-red-500">{integrationDashboard.failed_leads}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Pendientes:</span>
                <span className="font-semibold text-yellow-500">{integrationDashboard.pending_leads}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-mg-green">Integraciones por Tipo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(integrationDashboard.integrations_by_type || {}).map(([type, count]) => (
                <div key={type} className="flex justify-between items-center">
                  <div className="flex items-center">
                    {getIntegrationIcon(type)}
                    <span className="ml-2 text-sm">{type}</span>
                  </div>
                  <Badge className="bg-mg-green text-white">{count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-mg-green">Estados de Integración</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(integrationDashboard.integrations_by_status || {}).map(([status, count]) => (
                <div key={status} className="flex justify-between items-center">
                  <Badge className={INTEGRATION_STATUS_COLORS[status]}>{status}</Badge>
                  <span className="font-semibold">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Lista de integraciones
const IntegrationsList = ({ 
  integrations, 
  hasPermission, 
  onUpdateStatus, 
  onDelete, 
  onTest,
  setShowTestIntegrationDialog 
}) => (
  <div className="space-y-6">
    <h2 className="text-xl font-semibold text-mg-green">Integraciones Configuradas</h2>
    
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {integrations.map((integration) => (
        <Card key={integration.id} className="relative">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-mg-green rounded-lg flex items-center justify-center text-white">
                  {getIntegrationIcon(integration.integration_type)}
                </div>
                <div className="ml-3">
                  <CardTitle className="text-mg-green text-sm">{integration.name}</CardTitle>
                  <p className="text-xs text-gray-600">{integration.integration_type}</p>
                </div>
              </div>
              <Badge className={INTEGRATION_STATUS_COLORS[integration.status]}>
                {integration.status}
              </Badge>
            </div>
          </CardHeader>
          
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm text-gray-600">{integration.description}</p>
              
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div>
                  <span className="text-gray-500">Total Leads:</span>
                  <div className="font-semibold">{integration.total_leads_processed}</div>
                </div>
                <div>
                  <span className="text-gray-500">Exitosos:</span>
                  <div className="font-semibold text-green-500">{integration.successful_leads}</div>
                </div>
                <div>
                  <span className="text-gray-500">Fallidos:</span>
                  <div className="font-semibold text-red-500">{integration.failed_leads}</div>
                </div>
                <div>
                  <span className="text-gray-500">Duplicados:</span>
                  <div className="font-semibold text-yellow-500">{integration.duplicate_leads}</div>
                </div>
              </div>
              
              {integration.last_sync_at && (
                <div className="text-xs text-gray-500">
                  Último sync: {new Date(integration.last_sync_at).toLocaleString()}
                </div>
              )}
              
              {hasPermission(['Admin']) && (
                <div className="flex space-x-2 pt-2">
                  <Select
                    value={integration.status}
                    onValueChange={(value) => onUpdateStatus(integration.id, value)}
                  >
                    <SelectTrigger className="text-xs h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Inactive">Inactive</SelectItem>
                      <SelectItem value="Testing">Testing</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Button
                    onClick={() => {
                      // Set test data and open dialog
                      setShowTestIntegrationDialog(true);
                    }}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                  >
                    <Play className="h-3 w-3" />
                  </Button>
                  
                  <Button
                    onClick={() => onDelete(integration.id)}
                    variant="outline"
                    size="sm"
                    className="text-xs text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
      
      {integrations.length === 0 && (
        <div className="col-span-full text-center py-12 text-gray-500">
          <Link className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No hay integraciones configuradas</p>
        </div>
      )}
    </div>
  </div>
);

// Lista de leads entrantes
const IncomingLeadsList = ({ incomingLeads, hasPermission, onReprocess }) => (
  <div className="space-y-6">
    <h2 className="text-xl font-semibold text-mg-green">Leads Procesados</h2>
    
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Integración
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Datos Fuente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Recibido
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Procesado
                </th>
                {hasPermission(['Admin', 'Manager']) && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Acciones
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {incomingLeads.length === 0 ? (
                <tr>
                  <td colSpan={hasPermission(['Admin', 'Manager']) ? 6 : 5} className="px-6 py-12 text-center text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No hay leads procesados</p>
                  </td>
                </tr>
              ) : (
                incomingLeads.map((lead) => (
                  <tr key={lead.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {lead.integration_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge className={LEAD_PROCESSING_STATUS_COLORS[lead.status]}>
                        {lead.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
                      <div className="truncate">
                        {lead.source_data?.name || lead.source_data?.email || 'Datos disponibles'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(lead.received_at).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {lead.processed_at ? new Date(lead.processed_at).toLocaleString() : '-'}
                    </td>
                    {hasPermission(['Admin', 'Manager']) && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {lead.status === 'Failed' && (
                          <Button
                            onClick={() => onReprocess(lead.id)}
                            variant="outline"
                            size="sm"
                          >
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Reprocesar
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  </div>
);

// Configuración de integraciones
const IntegrationConfiguration = ({ 
  integrations, 
  selectedIntegration, 
  fieldMappings, 
  onSelectIntegration,
  onCreateFieldMapping,
  onDeleteFieldMapping,
  setShowFieldMappingDialog 
}) => (
  <div className="space-y-6">
    <h2 className="text-xl font-semibold text-mg-green">Configuración de Field Mappings</h2>
    
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Integration Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-mg-green">Seleccionar Integración</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {integrations.map((integration) => (
              <div
                key={integration.id}
                className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                  selectedIntegration?.id === integration.id
                    ? 'border-mg-green bg-mg-green/5'
                    : 'border-gray-200 hover:border-mg-green/50'
                }`}
                onClick={() => onSelectIntegration(integration)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-mg-green rounded-lg flex items-center justify-center text-white">
                      {getIntegrationIcon(integration.integration_type)}
                    </div>
                    <div className="ml-3">
                      <h3 className="font-semibold text-mg-green">{integration.name}</h3>
                      <p className="text-sm text-gray-600">{integration.integration_type}</p>
                    </div>
                  </div>
                  <Badge className={INTEGRATION_STATUS_COLORS[integration.status]}>
                    {integration.status}
                  </Badge>
                </div>
              </div>
            ))}
            
            {integrations.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Settings className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No hay integraciones disponibles</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Field Mappings */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-mg-green">
              {selectedIntegration ? `Field Mappings: ${selectedIntegration.name}` : 'Field Mappings'}
            </CardTitle>
            {selectedIntegration && (
              <Button
                onClick={() => {
                  // Set integration ID and open dialog
                  setShowFieldMappingDialog(true);
                }}
                variant="outline"
                size="sm"
                className="border-mg-green text-mg-green hover:bg-mg-green hover:text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Mapping
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {selectedIntegration ? (
            <div className="space-y-4">
              {fieldMappings.map((mapping) => (
                <div key={mapping.id} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Badge className="bg-blue-100 text-blue-800">
                          {mapping.mapping_type}
                        </Badge>
                        {mapping.required && (
                          <Badge className="bg-red-100 text-red-800">
                            Requerido
                          </Badge>
                        )}
                      </div>
                      
                      <div className="text-sm space-y-1">
                        <div>
                          <span className="font-medium">Fuente:</span> {mapping.source_field}
                        </div>
                        <div>
                          <span className="font-medium">Destino:</span> {mapping.target_field}
                        </div>
                        {mapping.default_value && (
                          <div>
                            <span className="font-medium">Valor por defecto:</span> {mapping.default_value}
                          </div>
                        )}
                        {mapping.transformation_rule && (
                          <div>
                            <span className="font-medium">Transformación:</span> {mapping.transformation_rule}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <Button
                      onClick={() => onDeleteFieldMapping(mapping.id)}
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              
              {fieldMappings.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Settings className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No hay field mappings configurados</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>Selecciona una integración para ver sus field mappings</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  </div>
);

export default IntegrationsView;